import { api } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";

const CHUNK = 1024 * 1024;
export const MAX_VIDEO_SECONDS = 15;

export const videoDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

export const videoPoster = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  const src = URL.createObjectURL(file);
  const end = (blob) => { clearTimeout(t); URL.revokeObjectURL(src); resolve(blob ? new File([blob], "vorschau.jpg", { type: "image/jpeg" }) : null); };
  const t = setTimeout(() => end(null), 15000);
  v.muted = true; v.playsInline = true; v.preload = "auto";
  v.onloadedmetadata = () => { v.currentTime = Math.min(1, (v.duration || 2) / 2); };
  v.onseeked = () => {
    const s = Math.min(1, 1280 / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * s); c.height = Math.round(v.videoHeight * s);
    if (!c.width) return end(null);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(end, "image/jpeg", 0.82);
  };
  v.onerror = () => end(null);
  v.src = src;
});

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const retriable = (e) => !e?.response || e.response.status >= 500 || e.response.status === 429;

async function withRetry(fn, tries = 6) {
  for (let a = 0; ; a++) {
    try { return await fn(); } catch (e) {
      if (a >= tries - 1 || !retriable(e)) throw e;
      await wait(Math.min(8000, 800 * 2 ** a));
    }
  }
}

const sendChunk = (base, id, file, i) => withRetry(() =>
  api.post(`${base}/${id}`, file.slice(i * CHUNK, (i + 1) * CHUNK), { params: { i }, timeout: 60000, headers: { "Content-Type": "application/octet-stream" } }));

const PARALLEL = 4;

export async function uploadChunked(base, file, onProgress) {
  const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
  const chunks = Math.ceil(file.size / CHUNK);
  let next = 0, done = 0;
  const worker = async () => {
    while (next < chunks) {
      const i = next++;
      await sendChunk(base, id, file, i);
      onProgress?.(Math.min(98, Math.round((++done / chunks) * 100)));
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, chunks) }, worker));
  const params = { content_type: file.type || "video/mp4", size: file.size, chunks };
  const finish = async () => {
    for (let p = 0; p < 120; p++) {
      const r = await withRetry(() => api.post(`${base}/${id}/finish`, null, { params, timeout: 180000 }));
      if (!r.data.pending) return r;
      await wait(3000);
    }
    throw new Error("Das Speichern dauert zu lange – bitte erneut versuchen");
  };
  let res;
  try { res = await finish(); } catch (e) {
    const missing = e?.response?.status === 409 && e.response.data?.detail?.missing;
    if (!missing) throw e;
    for (const i of missing) await sendChunk(base, id, file, i);
    try { res = await finish(); } catch (e2) {
      if (e2?.response?.status === 409) throw new Error("Das Video kam unvollständig an – bitte erneut hochladen");
      throw e2;
    }
  }
  if (res.data.size !== file.size) throw new Error("Das Video ist auf dem Server kürzer als das Original – bitte erneut hochladen");
  onProgress?.(99);
  return res.data.url;
}

export async function uploadStoryMedia(file, onProgress) {
  if (file.type.startsWith("image/")) return { kind: "image", url: (await uploadImage("/stories/upload/image", file)).url };
  return { kind: "video", url: await uploadChunked("/stories/upload/video", file, onProgress) };
}
